import { ValidationErrors } from "final-form";
import { get } from "lodash";

import { config } from "config/config";
import { parseRichTextFormatToHtml } from "helpers/tickets";
import { FormBlockType, IFormBlock } from "store/entities/contact-forms/interfaces";
import { plural } from "utils/string";

import { HTML_VALUE_BLOCKS, SIMPLE_VALUE_BLOCKS } from "./constants";

export const generateFormUrl = ({ licenseID, id }: { licenseID: number; id: string }): string =>
  `${config.contactFormsRootUrl}?licenseID=${licenseID}&contactFormID=${id}`;

export const generateEmbedCode = (fields: {
  licenseID: number;
  id: string;
}): string => `<iframe sandbox="allow-scripts allow-popups allow-forms allow-same-origin" width="100%" height="660px" style="border: 0; overflow: hidden; overflow-x: auto" src="${generateFormUrl(
  fields
)}">
    Your browser does not allow embedded content.
  </iframe>
`;

const countErrors = (arr: Array<{ value: string }>): Record<string, number> => {
  return arr.reduce(
    (acc, { value }) => {
      if (value.includes("Enter a max. of")) return { ...acc, length: (acc.length || 0) + 1 };
      if (value.includes("Enter")) return { ...acc, required: (acc.required || 0) + 1 };
      return acc;
    },
    {} as Record<string, number>
  );
};

type ErrorsMap = Record<string, string[]>;

export function getFormErrors(errors: ValidationErrors, fieldName: string, fieldType: FormBlockType): ErrorsMap {
  const errorsMap: ErrorsMap = {};
  switch (fieldType) {
    case "consent": {
      const consentErrorMessages: string[] = [];
      const requiredErrorMessage = get(errors, `${fieldName}.requiredError`);
      const htmlErrorMessage = get(errors, `${fieldName}.value.html`);
      if (requiredErrorMessage) {
        consentErrorMessages.push(requiredErrorMessage);
      }
      if (htmlErrorMessage) {
        consentErrorMessages.push(htmlErrorMessage);
      }
      errorsMap[fieldType] = consentErrorMessages;
      break;
    }
    case "paragraph": {
      const paragraphErrorMessages: string[] = [];
      const errorMessage = get(errors, `${fieldName}.value.html`);
      if (errorMessage) {
        paragraphErrorMessages.push(errorMessage);
      }
      errorsMap[fieldType] = paragraphErrorMessages;
      break;
    }
    case "header": {
      const headerErrorMessages: string[] = [];
      const errorMessage = get(errors, `${fieldName}.value`);
      if (errorMessage) {
        headerErrorMessages.push(errorMessage);
      }
      errorsMap[fieldType] = headerErrorMessages;
      break;
    }
    case "subject": {
      const subjectErrorMessages: string[] = [];
      const valueErrorMessage = get(errors, `${fieldName}.value`);
      if (valueErrorMessage) {
        subjectErrorMessages.push(valueErrorMessage);
      }
      const requiredErrorMessage = get(errors, `${fieldName}.requiredError`);
      if (requiredErrorMessage) {
        subjectErrorMessages.push(requiredErrorMessage);
      }
      errorsMap[fieldType] = subjectErrorMessages;
      break;
    }
    case "checkbox":
    case "radio":
    case "select": {
      const selectErrorMessages: string[] = [];
      const requiredErrorMessage = get(errors, `${fieldName}.requiredError`);
      const valueErrorMessage = get(errors, `${fieldName}.value`);
      const optionErrors = get(errors, `${fieldName}.options`)?.filter(Boolean);
      if (requiredErrorMessage) {
        selectErrorMessages.push(requiredErrorMessage);
      }
      if (valueErrorMessage) {
        selectErrorMessages.push(valueErrorMessage);
      }
      if (optionErrors?.length > 0) {
        const errorCounts = countErrors(optionErrors);
        if (errorCounts.length > 0) {
          selectErrorMessages.push(`${plural(errorCounts.length, "Choice", "Choices")} too long`);
        }
        if (errorCounts.required > 0) {
          selectErrorMessages.push(`Enter ${plural(errorCounts.required, "choice", "choices")}`);
        }
      }
      errorsMap[fieldType] = selectErrorMessages;
      break;
    }
    case "email": {
      const emailErrorMessages: string[] = [];
      const errorMessage = get(errors, `${fieldName}.value`);
      if (errorMessage) {
        emailErrorMessages.push(errorMessage);
      }
      const requiredErrorMessage = get(errors, `${fieldName}.requiredError`);
      const incorrectErrorMessage = get(errors, `${fieldName}.incorrectError`);
      if (requiredErrorMessage || incorrectErrorMessage) {
        emailErrorMessages.push(requiredErrorMessage || incorrectErrorMessage);
      }
      errorsMap[fieldType] = emailErrorMessages;
      break;
    }
    case "textarea":
    case "input":
    case "name": {
      const inputErrorMessages: string[] = [];
      const valueErrorMessage = get(errors, `${fieldName}.value`);
      if (valueErrorMessage) {
        inputErrorMessages.push(valueErrorMessage);
      }
      const requiredErrorMessage = get(errors, `${fieldName}.requiredError`);
      if (requiredErrorMessage) {
        inputErrorMessages.push(requiredErrorMessage);
      }
      errorsMap[fieldType] = inputErrorMessages;
      break;
    }
    case "submit": {
      const submitErrorMessages: string[] = [];
      const errorMessage = get(errors, `${fieldName}.value`);
      if (errorMessage) {
        submitErrorMessages.push(errorMessage);
      }
      const sendSuccessLabel = get(errors, `${fieldName}.sendSuccessLabel`);
      const sendErrorLabel = get(errors, `${fieldName}.sendErrorLabel`);
      if (sendSuccessLabel) {
        submitErrorMessages.push(sendSuccessLabel);
      }
      if (sendErrorLabel) {
        submitErrorMessages.push(sendErrorLabel);
      }
      errorsMap[fieldType] = submitErrorMessages;
      break;
    }
    case "options": {
      const optionsErrorMessages: string[] = [];
      const sendErrorMessage = get(errors, `${fieldName}.sendErrorMessage`);
      if (sendErrorMessage) {
        optionsErrorMessages.push(sendErrorMessage);
      }
      const confirmation = get(errors, `${fieldName}.confirmation`);
      if (confirmation) {
        optionsErrorMessages.push(confirmation);
      }
      errorsMap[fieldType] = optionsErrorMessages;
      break;
    }
  }
  return errorsMap;
}

const truncate = (str: string, maxLength = 50): string => {
  return str.length > maxLength ? str.slice(0, maxLength) + "..." : str;
};

export const stripHtml = (html: string): string => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

export function getFieldLabel(
  values: { fields: IFormBlock[] },
  fieldName: string,
  fieldType: FormBlockType,
  fieldLabel: string
): string {
  if (SIMPLE_VALUE_BLOCKS.includes(fieldType)) {
    return get(values, `${fieldName}.value`) || "";
  } else if (HTML_VALUE_BLOCKS.includes(fieldType)) {
    return truncate(stripHtml(parseRichTextFormatToHtml(get(values, `${fieldName}.value.html`) || [])), 45);
  }
  return fieldLabel;
}

export function hasTruthyValue(obj: unknown): boolean {
  if (obj == null) {
    return false;
  }

  if (Array.isArray(obj)) {
    return obj.some(item => hasTruthyValue(item));
  }

  if (typeof obj === "object") {
    return Object.values(obj).some(value => hasTruthyValue(value));
  }

  return Boolean(obj);
}
